import { isC3Experience } from "../../../shared/util/C3Utils";

define((require) => {
    "use strict";

    var CompanyModel                 = require("data/CompanyModel"),
        CompanySettings              = require("shared/ui/settings/CompanySettings"),
        IntegrationCollection        = require("editor/ui/settings/integrations/IntegrationCollection"),
        LoggedInUserState            = require("shared/ui/user/LoggedInUserState"),
        ProjectMetadataCollection    = require("ui/editor/project/ProjectMetadataCollection"),
        Promise                      = require("shared/util/Promise"),
        PrototypeActionCollection    = require("ui/experience/action/PrototypeActionCollection"),
        PrototypeWidgetCollection    = require("ui/experience/widgets/PrototypeWidgetCollection"),
        Time                         = require("shared/services/Time");

    /**
     * A <i>loader</i> for all global data to be loaded/cached before the user logs in the first time. <p>
     *
     * Based on the options supplied to <tt>loginUserFromPrincipal(principal, options)</tt>, relevant data will be
     * fetched, and the returned promise will resolve once all such data has loaded. <p>
     *
     * Do no use this class directly, already called where it needs to be called! Simply use <tt>LoggedInUserState</tt>
     * to listen for/use the data instead.
     *
     * @class LoggedInUserStateLoader
     */
    return {

        /**
         * Loads all data for the user associated with the specified principal, returning a promise that resolves
         * when all data is loaded. <p>
         *
         * Options have the following format:
         * <pre>
         *     {
         *         // Options to load the ExperienceMetadataCollection by, defaulting to {includeMeasureData: true}
         *         experiences:  {
         *              includeMeasureData: Boolean, // include measure data    (default false)
         *              pushedOnly:         Boolean  // only pushed experiences (default false)
         *         },
         *         images: Boolean, // load global image collection (default false)
         *     }
         * </pre>
         *
         * @param   {Principal} principal The principal for the user in question; never null.
         * @param   {*}         [options] The options describing what user state to load here; will default.
         * @returns {Promise} A promise that resolves when all user data is loaded.
         */
        load: function(principal, options) {
            // Set the user, but don't set the ready flag yet if different user...
            var user = principal.get("user"),
                companyId = user.get("companyId");

            // Already ready, i.e. data has already been fetched? Same company means data is ready...
            if (LoggedInUserState.get("ready")) {
                if (LoggedInUserState.get("user").get("companyId") !== companyId) {
                    LoggedInUserState.clear();
                } else {
                    LoggedInUserState.setUser(user);

                    return Promise.resolved(); // do not fire ready again, since only user changed...
                }
            }

            // First time, or different company...
            LoggedInUserState.setUser(user);

            options = options || {};

            // Fetch company model and global collections
            var company                      = new CompanyModel({id: companyId}),
                companySettings              = new CompanySettings({companyId: companyId}),
                projectMetadataCollection    = new ProjectMetadataCollection(), // optionally loaded
                prototypeActionCollection    = new PrototypeActionCollection([], {companyId: companyId}),
                prototypeWidgetCollection    = new PrototypeWidgetCollection([], {companyId: companyId}),
                integrations                 = new IntegrationCollection([], {companyId: companyId});

            var promises = [
                company.fetch(),
                companySettings.fetch(),
                integrations.fetch(),
                prototypeActionCollection.fetch(),
                prototypeWidgetCollection.fetch()
            ];

            // Update server time in the background, no need to wait for it...
            Time.update();

            // When global collections are done loading, set them with the logged in flag
            return Promise.all(promises).done(() => {
                LoggedInUserState.set({
                    company:                   company,
                    companySettings:           companySettings,
                    integrations:              integrations,
                    prototypeActionCollection: prototypeActionCollection,
                    prototypeWidgetCollection: prototypeWidgetCollection
                });

                if ((typeof options.projects === "undefined" || options.projects) && !isC3Experience()) {
                    promises.push(projectMetadataCollection.fetch().then(() => {
                        const projects = projectMetadataCollection.projects();
                        LoggedInUserState.set({
                            projects: projects,
                            ready: true
                        });
                    }));
                } else {
                    LoggedInUserState.set({
                        ready: true
                    });
                }

                const loggedInUser = LoggedInUserState.get("user");

                const pendoData = {
                    visitor: {
                        id: principal.get("userId"),
                        // We call the field username even though it is their email
                        username: loggedInUser.get("username"),
                        role: loggedInUser.get("role").name,
                        lastAccessed: new Date(loggedInUser.get("accessedAt")).toISOString(),
                        acctCreated: new Date(loggedInUser.get("createdAt")).toISOString(),
                        platform: "creator"
                    },

                    account: {
                        id: principal.get("user").get("companyId"),
                        companyName: LoggedInUserState.get("company").get("name")
                    }
                };

                if (window.pendo) {
                    window.pendo.initialize(pendoData);
                }
            });
        }
    };
});



// WEBPACK FOOTER //
// ./src/editor/ui/user/LoggedInUserStateLoader.js